import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { AuthTags } from '~constants/authTags';

import { ClickableIcon, Select } from '~components';
import { faArrowRight } from '~components/icon';
import { Row, TableView } from '~components/table-view';

import { columnsFactory } from '~support-at-home/forms/settings/accessSettingsList';

import { getAll as getUsers } from '~care-workers/actions/index';
import { accessTagsApi, useGetAccessTagsQuery } from '~support-at-home/actions/accessTags';
import {
  useCreateUserTagMutation,
  useDeleteUserTagMutation,
  useGetUserTagsQuery,
  userTagsApi,
} from '~support-at-home/actions/userTags';

import { selectAccessTagAdminUsers, selectAccessTagQuickAddOptions } from '~care-workers/selectors/users';
import { selectUserTags } from '~main/selectors/login';

interface ComponentProps {
  isActive: boolean;
  module?: string;
  name?: string;
  adminTag?: string;
  explicitOnly?: string[];
  // Permissions admins auto-hold only when they have unlocking_permissions (e.g. forms Create).
  unlockOnly?: string[];
  // Display-only label overrides keyed by permission (e.g. { View: 'View/Fill' }).
  permissionLabels?: Record<string, string>;
  canManage?: boolean;
}

export const AccessSettings: React.FC<ComponentProps> = ({
  isActive,
  module = 'supportathome',
  name = 'supplierinvoices',
  adminTag = AuthTags.SUPPORTATHOME.SUPPLIERINVOICES.ADMIN,
  explicitOnly = ['Override'],
  unlockOnly = [],
  permissionLabels = {},
  canManage,
}) => {
  const dispatch = useAppDispatch();

  const TAG_CONFIGS = useMemo(() => ({ module, name }), [module, name]);

  const userPermissions = useAppSelector(selectUserTags);
  const isAdmin = canManage ?? userPermissions.includes(adminTag);

  const quickAddUserOptions = useAppSelector(selectAccessTagQuickAddOptions);
  const adminUsers = useAppSelector(selectAccessTagAdminUsers);

  const { data: userTags } = useGetUserTagsQuery(TAG_CONFIGS, { skip: !isActive });
  const { data: lookupTags } = useGetAccessTagsQuery(TAG_CONFIGS, { skip: !isActive });

  const [createUserTag] = useCreateUserTagMutation();
  const [deleteUserTag] = useDeleteUserTagMutation();

  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [selectedPermission, setSelectedPermission] = useState<string | null>(null);

  const permissionList =
    lookupTags?.map((tag) => ({ value: tag.id, label: permissionLabels[tag.permission] ?? tag.permission })) ?? [];
  const userTagRows: Row[] = (userTags as Row[])?.map((row) => ({ ...row, id: row.user_id })) ?? [];

  const adminUserIds = new Set(adminUsers.map(({ id }) => id));

  const adminRows = adminUsers.map(({ id, full_name, unlocking_permissions }) => {
    const existingRow = userTagRows.find((r) => r.user_id === id);
    return {
      id,
      user_id: id,
      full_name,
      tags: existingRow?.tags ?? [],
      editable: false,
      unlocking_permissions,
    };
  });

  const nonAdminRows = userTagRows
    .filter((row) => !adminUserIds.has(row.user_id))
    .map((row) => ({ ...row, editable: true }));

  const tableData = [...adminRows, ...nonAdminRows];

  useEffect(() => {
    dispatch(
      getUsers({
        disabled: false,
        is_system_only: false,
        attributes: ['id', 'full_name', 'accounts_access_enabled', 'unlocking_permissions'],
        sort: ['accounts_access_enabled', 'unlocking_permissions', 'full_name.asc'],
      }),
    );
  }, []);

  useEffect(() => {
    if (!isActive) {
      dispatch(userTagsApi.util.invalidateTags([{ type: 'userTags', id: 'LIST' }]));
      dispatch(accessTagsApi.util.invalidateTags([{ type: 'accessTags', id: 'LIST' }]));
    }
  }, [isActive]);

  const handleAddPermission = () => {
    if (selectedUser == null || !selectedPermission) return;
    createUserTag({ user_id: selectedUser, tag_id: selectedPermission });
  };

  const handleDeleteRow = async (row: Row) => {
    if (!row) return;

    const { user_id } = row;
    const tagIds = [...new Set(row.tags.map((t: { tag_id: string }) => t.tag_id))] as string[];
    deleteUserTag({ user_id: Number(user_id), tags: tagIds });
  };

  const columns = columnsFactory(lookupTags || [], handleDeleteRow, isAdmin, explicitOnly, unlockOnly, permissionLabels);

  return (
    <div>
      {isAdmin && (
        <div className="util-row">
          <div className="fw-bold">Quick Add</div>
          <Select
            id="user"
            name="User"
            options={quickAddUserOptions}
            isClearable
            className="slim-input"
            value={selectedUser}
            onChange={(_, value) => setSelectedUser(value)}
          />
          <Select
            id="permission"
            name="Permission"
            options={permissionList}
            isClearable
            className="slim-input"
            value={selectedPermission}
            onChange={(_, value) => setSelectedPermission(value)}
          />
          <ClickableIcon
            onClick={handleAddPermission}
            id="add-permission"
            toolTip="Add Permission To User"
            icon={faArrowRight}
            disabled={selectedUser == null || !selectedPermission}
          />
        </div>
      )}
      <TableView id="access-settings-table" columns={columns} data={tableData} readonly />
    </div>
  );
};

export default AccessSettings;
